import { Flex, ResponsiveValue } from '@chakra-ui/react'
import React from 'react'
import IconCallStop from '~/assets/svgs/icon-call-stop.svg?react'
import IconCall from '~/assets/svgs/icon-call.svg?react'
import IconIncomingReject from '~/assets/svgs/icon-incoming-reject.svg?react'
import IconPhoneHoldHeavy from '~/assets/svgs/icon-phone-hold-heavy.svg?react'
import { TrackedParkingLot } from '~/atoms/WebphoneState'
import { MangoButtonVariant } from '~/components/shared/componentStyles/MangoButton.style'
import { MangoButton, MangoButtonProps } from '~/components/shared/MangoButton'
import { useMainWindow } from '~/hooks/useElectronWindow'
import { useSharedVariable } from '~/hooks/useSharedVariable'
import { IncomingCallButtonVariants } from '~/pages/WebPhone/componentStyles/IncomingCallButton.style'
import { ParkCallButton } from '~/pages/WebPhone/IncomingCallButton'
import { Size } from '~/types/ThemeTypes'
import { isElectron } from '~/utils/common'
import { noop } from '~/utils/functions'
import { removeItemFromQueueById } from '~/utils/popupQueueUtils'

import { CallMetadata, PopupQueueItem } from '@mangovoice/types'

type CallAnswerType =
  | 'answer'
  | 'answerWithHold'
  | 'answerWithEnd'
  | 'answerWithPark'

const customMangoButtonProps = {
  size: Size.md,
  leftIconProps: {
    boxSize: 4,
  },
  rightIconProps: {
    boxSize: 4,
  },
  fontSize: '.25rem',
  padding: '0.5rem .75rem',
  whiteSpace: 'break-spaces',
  width: 'auto',
  flexGrow: 1,
} as Partial<MangoButtonProps>

export const MangoButtonPopup = (props: {
  onClick: () => void
  title: string
  leftIcon?: SVGComponent
  rightIcon?: SVGComponent
  variant?: MangoButtonVariant
  size?: ResponsiveValue<Size.md | Size.sm>
}) => {
  const { title, onClick, variant, leftIcon, rightIcon, size } = props

  return (
    <MangoButton
      leftIcon={leftIcon ?? IconCall}
      rightIcon={rightIcon}
      variant={variant ?? MangoButtonVariant.Default_300}
      onClick={onClick}
      {...customMangoButtonProps}
      size={size ?? Size.md}
    >
      {title}
    </MangoButton>
  )
}

export const PopupCallButtons = (props: {
  queueItem: PopupQueueItem
  isExtraLarge?: boolean
}) => {
  const { queueItem, isExtraLarge } = props

  const buttonSize = !isExtraLarge ? Size.sm : Size.md

  const {
    focusMainWindow,
    answerPhoneCall,
    rejectPhoneCall,
    parkCall,
    answerCallWithHold,
    answerCallWithEnd,
  } = useMainWindow()
  const [parkingLots] = useSharedVariable<TrackedParkingLot[]>('ParkingLots')
  const [callMetadata] = useSharedVariable<CallMetadata>('CallMetadata')
  const [callQueue, setCallQueue] =
    useSharedVariable<PopupQueueItem[]>('CallQueue')

  const { hasNoActiveCall, canParkOrHoldCall, mustRejectOrEndCall } =
    callMetadata || {}

  const handleAnswer = (
    queueItem: PopupQueueItem,
    answerType: CallAnswerType = 'answer',
  ) => {
    if (queueItem && isElectron) {
      setCallQueue(removeItemFromQueueById(callQueue || [], queueItem._id))
      focusMainWindow()

      switch (answerType) {
        case 'answerWithHold':
          answerCallWithHold()
          break
        case 'answerWithEnd':
          answerCallWithEnd()
          break
        case 'answerWithPark':
          parkCall()
          break
        default:
          answerPhoneCall()
          break
      }
    }
  }

  const handleReject = (queueItem: PopupQueueItem) => {
    if (queueItem && isElectron) {
      setCallQueue(removeItemFromQueueById(callQueue || [], queueItem._id))
      rejectPhoneCall()
    }
  }

  return (
    <Flex
      direction="row"
      flex="1"
      gap={3}
      flexGrow={1}
      flexWrap={'wrap'}
      paddingTop={1}
      justifyContent={'space-around'}
      alignItems={'center'}
    >
      {hasNoActiveCall && (
        <>
          <MangoButtonPopup
            onClick={() => handleAnswer(queueItem, 'answer')}
            title={'Accept'}
            variant={MangoButtonVariant.Success_300}
            size={buttonSize}
          />
          <MangoButtonPopup
            leftIcon={IconIncomingReject}
            onClick={() => handleReject(queueItem)}
            title={'Reject'}
            variant={MangoButtonVariant.Destructive_100}
            size={buttonSize}
          />
        </>
      )}
      {mustRejectOrEndCall && (
        <>
          <MangoButtonPopup
            leftIcon={IconCallStop}
            onClick={() => handleAnswer(queueItem, 'answerWithEnd')}
            title={'End & Accept'}
            variant={MangoButtonVariant.Success_300}
            size={buttonSize}
          />
          <MangoButtonPopup
            leftIcon={IconIncomingReject}
            onClick={() => handleReject(queueItem)}
            title={'Reject'}
            variant={MangoButtonVariant.Destructive_100}
            size={buttonSize}
          />
        </>
      )}
      {canParkOrHoldCall && (
        <>
          <MangoButtonPopup
            leftIcon={IconPhoneHoldHeavy}
            onClick={() => handleAnswer(queueItem, 'answerWithHold')}
            title={'Hold & Accept'}
            variant={MangoButtonVariant.Success_300}
            size={buttonSize}
          />
          <ParkCallButton
            isInPopup
            variant={IncomingCallButtonVariants.ParkAndAccept}
            onClick={noop}
            onClickParkingLot={() => handleAnswer(queueItem, 'answerWithPark')}
            parkingLots={parkingLots || []}
            mangoButtonProps={{
              ...customMangoButtonProps,
              size: buttonSize,
            }}
          >
            Park & Accept
          </ParkCallButton>
        </>
      )}
    </Flex>
  )
}
